import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Icon,
  Modal,
  Notice,
  PageHeader,
  Panel,
  Toggle,
} from "../components/ui";
import RealFlowEditor from "../../screens/FlowEditor.tsx";
import { Oncall } from "./Oncall";
import { People } from "./People";
import { api, ApiError } from "../../lib/api.ts";
import type {
  AddNumberRequest,
  BrandItem,
  ConnectIntegrationRequest,
  FlowListItem,
  FlowsResponse,
  FlowTemplateCard,
  IntegrationItem,
  IntegrationsResponse,
  InviteUserRequest,
  PhoneNumberItem,
  PolicyPatchRequest,
  PolicyResponse,
  Role,
  UpdateUserRequest,
  UserItem,
  VoiceDelivery,
  VoiceOption,
  VoicesResponse,
} from "../../lib/api.ts";
import {
  itemsOf,
  qk,
  useDemoMode,
  useMutate,
  usePermissions,
  useResource,
  useSession,
} from "../../lib/useApi.ts";

const ROLES: Role[] = ["owner", "admin", "dispatcher", "viewer"];
const roleLabel = (r: Role): string => r.charAt(0).toUpperCase() + r.slice(1);
export function ConfigurePage({ page }: { page: string }) {
  return (
    <>
      <nav className="config-nav" aria-label="Configuration">
        {[
          { label: "Policies", to: "/policies", icon: "sliders" },
          { label: "Flows", to: "/flows", icon: "flow" },
          { label: "Voices", to: "/voices", icon: "audio" },
          { label: "Integrations", to: "/integrations", icon: "link" },
          { label: "People", to: "/people", icon: "users" },
          { label: "On-call", to: "/oncall", icon: "bell" },
          { label: "Settings", to: "/settings", icon: "settings" },
        ].map((n) => (
          <NavLink key={n.to} to={n.to}>
            <Icon name={n.icon} size={17} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      {page === "policies" ? (
        <Policies />
      ) : page === "flows" ? (
        <Flows />
      ) : page === "flow" ? (
        <RealFlowEditor />
      ) : page === "voices" ? (
        <Voices />
      ) : page === "integrations" ? (
        <Integrations />
      ) : page === "oncall" ? (
        <Oncall />
      ) : page === "people" ? (
        <People />
      ) : (
        <Settings />
      )}
    </>
  );
}
function Policies() {
  const query = useResource<PolicyResponse>(qk.policy, () => api.policy(), { staleTimeMs: 30_000 });
  if (query.isLoading) {
    return (
      <div className="page-status" role="status" aria-live="polite">
        <Icon name="loader" className="spin" size={26} />
        <p>Loading policies…</p>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Panel title="Policies unavailable">
        <p className="muted">
          {(query.error as ApiError | null)?.detail || "The console could not reach NightDesk."}
        </p>
        <Button variant="primary" loading={query.isFetching} onClick={() => void query.refetch()}>
          <Icon name="refresh" /> Try again
        </Button>
      </Panel>
    );
  }
  return <PolicyForm policy={query.data} />;
}

type VerifyStrength = "id_only" | "id_plus_one" | "two_details";

const VERIFY_FACTORS: { key: PolicyResponse["verification"]["factors"][number]; label: string }[] = [
  { key: "surname", label: "Last name on the account" },
  { key: "service_zip", label: "Service ZIP code" },
  { key: "account_last4", label: "Account number" },
  { key: "house_number_street", label: "Service address" },
  { key: "email", label: "Email on file" },
];

function PolicyForm({ policy }: { policy: PolicyResponse }) {
  const { can } = usePermissions();
  const mayWrite = can("policy.write");
  const [draft, setDraft] = useState<PolicyResponse>(() => structuredClone(policy));
  const [keyword, setKeyword] = useState("");

  const save = useMutate<PolicyResponse, PolicyPatchRequest>({
    mutationFn: (body) => api.updatePolicy(body),
    success: "Policy saved",
    invalidate: [qk.policy],
    onSuccess: (fresh) => setDraft(structuredClone(fresh)),
  });

  function onSave() {
    if (!mayWrite) return;
    const patch: PolicyPatchRequest = {
      ticketWrite: draft.ticketWrite,
      restoreSms: draft.restoreSms,
      callbackPromise: draft.callbackPromise,
      standbyMode: draft.standbyMode,
      bargeIn: draft.bargeIn,
      emergencyKeywords: draft.emergencyKeywords,
      speakableFacts: {
        officeHoursText: draft.speakableFacts.officeHoursText,
        paymentPortalUrl: draft.speakableFacts.paymentPortalUrl,
        serviceAreaText: draft.speakableFacts.serviceAreaText,
      },
      verification: {
        required: draft.verification.required,
        aniCounts: draft.verification.aniCounts,
        factors: draft.verification.factors,
      },
      retentionDaysRecordings: draft.retentionDaysRecordings,
      greeting: draft.greeting,
    };
    save.mutate(patch);
  }

  const set = <K extends keyof PolicyResponse>(k: K, v: PolicyResponse[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const setFact = (k: keyof PolicyResponse["speakableFacts"], v: string) =>
    setDraft((d) => ({ ...d, speakableFacts: { ...d.speakableFacts, [k]: v.trim() ? v : null } }));

  // How callers are verified, expressed as one plain-language choice over (required, aniCounts).
  const verifyStrength: VerifyStrength =
    draft.verification.required <= 1 ? "id_only" : draft.verification.aniCounts ? "id_plus_one" : "two_details";
  const setVerifyStrength = (s: VerifyStrength) =>
    setDraft((d) => ({
      ...d,
      verification: {
        ...d.verification,
        required: s === "id_only" ? 1 : 2,
        aniCounts: s !== "two_details",
      },
    }));
  const toggleFactor = (f: PolicyResponse["verification"]["factors"][number]) =>
    setDraft((d) => {
      const has = d.verification.factors.includes(f);
      const factors = has ? d.verification.factors.filter((x) => x !== f) : [...d.verification.factors, f];
      return { ...d, verification: { ...d.verification, factors } };
    });

  return (
    <>
      <PageHeader
        eyebrow="Configure"
        title="Rules of the line"
        description="Set what the assistant may do, and who it pages."
        actions={
          <Button variant="primary" disabled={!mayWrite} loading={save.isPending} onClick={onSave}>
            Save changes
          </Button>
        }
      />
      {!mayWrite && <Notice>You do not have permission to change policies. Values are shown read-only.</Notice>}
      <div className="grid-two">
        <div className="stack">
          <Panel title="What happens without approval">
            <div className="panel-body">
              <Field label="Create support tickets">
                <select
                  disabled={!mayWrite}
                  value={draft.ticketWrite}
                  onChange={(e) => set("ticketWrite", e.target.value as PolicyResponse["ticketWrite"])}
                >
                  <option value="proposal">Propose for approval</option>
                  <option value="live">Create directly</option>
                  <option value="local">Keep locally only</option>
                </select>
              </Field>
              <Field label="Restoration texts">
                <select
                  disabled={!mayWrite}
                  value={draft.restoreSms}
                  onChange={(e) => set("restoreSms", e.target.value as PolicyResponse["restoreSms"])}
                >
                  <option value="proposal">Propose for approval</option>
                  <option value="allowed">Allow after restoration</option>
                  <option value="off">Off</option>
                </select>
              </Field>
              <Field label="Callback promises">
                <select
                  disabled={!mayWrite}
                  value={draft.callbackPromise}
                  onChange={(e) => set("callbackPromise", e.target.value as PolicyResponse["callbackPromise"])}
                >
                  <option value="proposal">Propose for approval</option>
                  <option value="allowed">Allow</option>
                  <option value="off">Off</option>
                </select>
              </Field>
              <p className="readonly-note">
                <Icon name="lock" size={15} />
                Equipment-changing actions are disabled by platform policy (CPE actions: off).
              </p>
            </div>
          </Panel>
          <Panel title="How the assistant runs">
            <div className="panel-body">
              <Field label="Daytime standby mode">
                <select
                  disabled={!mayWrite}
                  value={draft.standbyMode}
                  onChange={(e) => set("standbyMode", e.target.value as PolicyResponse["standbyMode"])}
                >
                  <option value="assist_lite">Assist-lite</option>
                  <option value="full_ai">Full AI</option>
                </select>
              </Field>
              <Field label="How a caller interrupts">
                <select
                  disabled={!mayWrite}
                  value={draft.bargeIn}
                  onChange={(e) => set("bargeIn", e.target.value as PolicyResponse["bargeIn"])}
                >
                  <option value="asr">Speak over (ASR)</option>
                  <option value="dtmf">Press a key (DTMF)</option>
                </select>
              </Field>
            </div>
          </Panel>
          <Panel title="What callers hear first">
            <div className="panel-body">
              <Field label="Greeting when the assistant answers">
                <textarea
                  rows={2}
                  disabled={!mayWrite}
                  placeholder="Thanks for calling {brand} after-hours support. How can I help you {time of day}?"
                  value={draft.greeting}
                  onChange={(e) => set("greeting", e.target.value)}
                />
              </Field>
              <p className="readonly-note">
                Use <strong>{"{brand}"}</strong> for your name and <strong>{"{time of day}"}</strong> — it becomes
                "this morning", "this afternoon", "this evening", or "tonight" based on when the call comes in. Leave
                blank to use the default.
              </p>
            </div>
          </Panel>
          <Panel title="How callers are verified">
            <div className="panel-body">
              <Field label="Before the assistant shares account details, the caller must give">
                <select
                  disabled={!mayWrite}
                  value={verifyStrength}
                  onChange={(e) => setVerifyStrength(e.target.value as VerifyStrength)}
                >
                  <option value="id_only">Their phone number only (caller ID)</option>
                  <option value="id_plus_one">Caller ID plus one spoken detail</option>
                  <option value="two_details">Two spoken details (ignore caller ID)</option>
                </select>
              </Field>
              {verifyStrength !== "id_only" && (
                <div className="field">
                  <span>Details the assistant will accept</span>
                  <div className="stack">
                    {VERIFY_FACTORS.map((f) => (
                      <Toggle
                        key={f.key}
                        label={f.label}
                        checked={draft.verification.factors.includes(f.key)}
                        disabled={!mayWrite}
                        onChange={() => toggleFactor(f.key)}
                      />
                    ))}
                  </div>
                </div>
              )}
              <p className="readonly-note">
                A caller whose number isn't on the account is asked for these details by name before anything is shared.
              </p>
            </div>
          </Panel>
          <Panel title="Call recordings">
            <div className="panel-body">
              <Field label="Keep call recordings for">
                <select
                  disabled={!mayWrite}
                  value={String(draft.retentionDaysRecordings)}
                  onChange={(e) => set("retentionDaysRecordings", Number(e.target.value))}
                >
                  <option value="1">1 day (24 hours)</option>
                  <option value="2">2 days (48 hours)</option>
                  <option value="3">3 days</option>
                  <option value="7">7 days</option>
                  <option value="14">14 days</option>
                  <option value="30">30 days</option>
                  <option value="60">60 days</option>
                  <option value="90">90 days</option>
                </select>
              </Field>
              <p className="readonly-note">
                After this, the recording audio is deleted automatically. The call transcript is kept and stays the record of what was said.
              </p>
            </div>
          </Panel>
          <Panel title="Facts the assistant can say">
            <div className="panel-body">
              <Field label="Office hours">
                <input
                  disabled={!mayWrite}
                  value={draft.speakableFacts.officeHoursText ?? ""}
                  onChange={(e) => setFact("officeHoursText", e.target.value)}
                />
              </Field>
              <Field label="Payment portal URL">
                <input
                  type="url"
                  disabled={!mayWrite}
                  value={draft.speakableFacts.paymentPortalUrl ?? ""}
                  onChange={(e) => setFact("paymentPortalUrl", e.target.value)}
                />
              </Field>
              <Field label="Service area">
                <input
                  disabled={!mayWrite}
                  value={draft.speakableFacts.serviceAreaText ?? ""}
                  onChange={(e) => setFact("serviceAreaText", e.target.value)}
                />
              </Field>
              <p className="readonly-note">
                <Icon name="lock" size={15} />
                Restoration estimates are entered on the Outage screen, never here.
              </p>
            </div>
          </Panel>
        </div>
        <div className="stack">
          <Panel title="Escalation ladder" action={<Badge tone="blue">Voicemail first</Badge>}>
            <div className="panel-body">
              <p className="muted">
                The caller leaves a message; the technician is paged and can call back. Calls are never
                transferred live. This ladder is configured by your administrator.
              </p>
              <ol className="step-list">
                {policy.escalation.ladder.map((step) => (
                  <li key={step.index}>
                    <span className="step-index">{step.index + 1}</span>
                    <div>
                      {step.channel} → {step.target}
                      <small>
                        {step.afterMinutes === 0 ? "Immediately" : `After ${step.afterMinutes} min`}
                      </small>
                    </div>
                  </li>
                ))}
              </ol>
              <p className="readonly-note">
                <Icon name="lock" size={15} />
                Worst case: on-call is reached within {policy.escalation.worstCaseMinutes} minutes.
              </p>
            </div>
          </Panel>
          <Panel title="Urgent situations">
            <div className="panel-body">
              <span className="eyebrow">You can edit these</span>
              <div className="button-row" style={{ marginTop: 8 }}>
                {draft.emergencyKeywords.length ? (
                  draft.emergencyKeywords.map((k) => (
                    <Badge key={k} dot>
                      {k}
                      {mayWrite && (
                        <button
                          className="chip-x"
                          aria-label={`Remove ${k}`}
                          onClick={() =>
                            set("emergencyKeywords", draft.emergencyKeywords.filter((x) => x !== k))
                          }
                        >
                          <Icon name="close" size={12} />
                        </button>
                      )}
                    </Badge>
                  ))
                ) : (
                  <span className="muted">None added</span>
                )}
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const k = keyword.trim();
                  if (k && !draft.emergencyKeywords.includes(k)) {
                    set("emergencyKeywords", [...draft.emergencyKeywords, k]);
                    setKeyword("");
                  }
                }}
                className="toolbar margin-top"
              >
                <input
                  className="input"
                  aria-label="Additional urgent keyword"
                  value={keyword}
                  disabled={!mayWrite}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="Add an urgent situation"
                />
                <Button type="submit" icon="plus" disabled={!mayWrite || !keyword.trim()}>
                  Add
                </Button>
              </form>
              {policy.lockedEmergencyCore.length > 0 && (
                <>
                  <span className="eyebrow" style={{ marginTop: 16, display: "block" }}>
                    Platform-managed core (read-only)
                  </span>
                  <div className="button-row" style={{ marginTop: 8 }}>
                    {policy.lockedEmergencyCore.map((k) => (
                      <Badge key={k} tone="neutral">
                        {k}
                      </Badge>
                    ))}
                  </div>
                </>
              )}
              <p className="readonly-note">
                <Icon name="lock" size={15} />
                Core emergency guidance is platform-managed and cannot be removed.
              </p>
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
const FLOWS_KEY = ["flows"] as const;

function Flows() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const mayWrite = can("policy.write");
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [retiring, setRetiring] = useState<FlowListItem | null>(null);

  const query = useResource<FlowsResponse>(FLOWS_KEY, () => api.flows.list(), { staleTimeMs: 30_000 });
  const templatesQ = useResource(["flow-templates"], () => api.flows.templates(), { enabled: templatesOpen });

  const create = useMutate<{ item: { id: string } }, FlowTemplateCard | null>({
    mutationFn: (card) => api.flows.create(card ? { template: card.code } : {}),
    success: "Flow created",
    invalidate: [FLOWS_KEY],
    onSuccess: (res) => {
      setTemplatesOpen(false);
      navigate(`/flows/${res.item.id}`);
    },
  });
  const pin = useMutate<{ item: FlowListItem }, FlowListItem>({
    mutationFn: (f) => api.flows.pin(f.id, { pinned: !f.pinned }),
    success: (_d, f) => (f.pinned ? "Flow unpinned" : "Flow pinned"),
    invalidate: [FLOWS_KEY],
    touchesTonight: true,
  });
  const reorder = useMutate<{ ok: true }, string[]>({
    mutationFn: (ids) => api.flows.reorder(ids),
    success: "Pin order updated",
    invalidate: [FLOWS_KEY],
    touchesTonight: true,
  });
  const retire = useMutate<{ ok: true }, string>({
    mutationFn: (id) => api.flows.retire(id),
    success: "Flow retired",
    invalidate: [FLOWS_KEY],
    touchesTonight: true,
    onSuccess: () => setRetiring(null),
  });

  const items = (query.data?.items ?? []).slice().sort((a, b) => Number(b.pinned) - Number(a.pinned) || a.pinOrder - b.pinOrder);
  const pinnedIds = items.filter((f) => f.pinned).map((f) => f.id);

  function moveEarlier(id: string) {
    const i = pinnedIds.indexOf(id);
    if (i < 1) return;
    const next = pinnedIds.slice();
    [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
    reorder.mutate(next);
  }

  return (
    <>
      <PageHeader
        eyebrow="Configure"
        title="Call flows"
        description="Design the conversation. Pin the services you use often."
        actions={
          <Button variant="primary" icon="plus" disabled={!mayWrite} onClick={() => setTemplatesOpen(true)}>
            New flow
          </Button>
        }
      />
      <Panel>
        {query.isLoading ? (
          <div className="page-status" role="status" aria-live="polite">
            <Icon name="loader" className="spin" size={26} />
            <p>Loading flows…</p>
          </div>
        ) : query.isError ? (
          <EmptyState
            icon="flow"
            title="Could not load flows"
            text={(query.error as ApiError | null)?.detail || "The console could not reach NightDesk."}
            action={
              <Button variant="primary" onClick={() => void query.refetch()}>
                Try again
              </Button>
            }
          />
        ) : items.length ? (
          items.map((f) => (
            <article className="record-row" key={f.id}>
              <span className="service-icon">
                <Icon name={f.pinIcon || (f.kind === "action" ? "message" : "flow")} size={30} />
              </span>
              <div className="record-body">
                <div className="record-heading">
                  <Link to={`/flows/${f.id}`}>
                    <h3>{f.name}</h3>
                  </Link>
                  <Badge tone={f.armed ? "green" : f.graphStale ? "amber" : "neutral"}>
                    {f.armed ? "Armed" : f.graphStale ? "Needs update" : "Ready"}
                  </Badge>
                  <Badge>{f.kind === "action" ? "Call action" : "Standing service"}</Badge>
                </div>
                <p className="muted">{f.trigger}</p>
                <div className="record-meta">
                  <span>{f.stepCount} steps</span>
                  <span>Version {f.version}</span>
                  {(f.kind === "posture" || f.pinned) && (
                    <span>{f.pinned ? "Pinned to Overview" : "Not pinned"}</span>
                  )}
                </div>
              </div>
              <div className="button-row">
                {/* Overview's Quick activate only shows standing services, so Pin on a call
                    action did nothing anyone could see. Offer it on standing services, and
                    keep it on anything already pinned so it can still be unpinned. */}
                {(f.kind === "posture" || f.pinned) && (
                  <Button
                    icon={f.pinned ? "check" : "plus"}
                    disabled={!mayWrite || (pin.isPending && pin.variables?.id === f.id)}
                    loading={pin.isPending && pin.variables?.id === f.id}
                    onClick={() => pin.mutate(f)}
                  >
                    {f.pinned ? "Pinned" : "Pin"}
                  </Button>
                )}
                {f.pinned && pinnedIds.indexOf(f.id) > 0 && (
                  <Button
                    icon="back"
                    aria-label={`Move ${f.name} earlier`}
                    disabled={!mayWrite || reorder.isPending}
                    onClick={() => moveEarlier(f.id)}
                  />
                )}
                <Link className="button" to={`/flows/${f.id}`}>
                  Edit
                </Link>
                <Button
                  variant="ghost"
                  icon="trash"
                  aria-label={`Retire ${f.name}`}
                  disabled={!mayWrite || f.armed}
                  onClick={() => setRetiring(f)}
                />
              </div>
            </article>
          ))
        ) : (
          <EmptyState icon="flow" title="No flows yet" text="Start from a template to design your first call flow." />
        )}
      </Panel>
      <Notice>
        Standing services can remain armed. One-time call actions run from an individual call record.
      </Notice>

      <Modal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        title="Start a call flow"
        description="Use a template, then adjust its wording and steps."
        wide
      >
        {templatesQ.isLoading ? (
          <div className="page-status" role="status" aria-live="polite">
            <Icon name="loader" className="spin" size={22} />
            <p>Loading templates…</p>
          </div>
        ) : (
          <div className="grid-three">
            {(templatesQ.data?.templates ?? []).map((t) => (
              <button
                className="flow-template"
                key={t.code}
                disabled={create.isPending || !mayWrite}
                onClick={() => create.mutate(t)}
              >
                <span className="icon-well">
                  <Icon name={t.kind === "action" ? "message" : "flow"} />
                </span>
                <h3 style={{ marginTop: 16 }}>{t.name}</h3>
                <p>{t.description}</p>
                <span className="text-button">
                  Use template <Icon name="arrow" size={16} />
                </span>
              </button>
            ))}
            <button className="flow-template" disabled={create.isPending || !mayWrite} onClick={() => create.mutate(null)}>
              <span className="icon-well">
                <Icon name="plus" />
              </span>
              <h3 style={{ marginTop: 16 }}>Blank flow</h3>
              <p>Start from an empty entry node and build it yourself.</p>
              <span className="text-button">
                Start blank <Icon name="arrow" size={16} />
              </span>
            </button>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(retiring)}
        onClose={() => setRetiring(null)}
        title="Retire this flow?"
        description="It will be removed from the active list and Overview shortcuts."
        footer={
          <>
            <Button onClick={() => setRetiring(null)}>Cancel</Button>
            <Button
              variant="danger"
              disabled={!mayWrite || (retiring?.armed ?? false)}
              loading={retire.isPending}
              onClick={() => retiring && retire.mutate(retiring.id)}
            >
              Retire flow
            </Button>
          </>
        }
      >
        <p>{retiring?.name}</p>
        {retiring?.armed && <Notice tone="amber">Turn off an armed flow before retiring it.</Notice>}
      </Modal>
    </>
  );
}
function Voices() {
  const query = useResource<VoicesResponse>(["voices"], () => api.voices.list(), { staleTimeMs: 60_000 });
  if (query.isLoading) {
    return (
      <div className="page-status" role="status" aria-live="polite">
        <Icon name="loader" className="spin" size={26} />
        <p>Loading voices…</p>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Panel title="Voices unavailable">
        <p className="muted">{(query.error as ApiError | null)?.detail || "The console could not reach NightDesk."}</p>
        <Button variant="primary" loading={query.isFetching} onClick={() => void query.refetch()}>
          <Icon name="refresh" /> Try again
        </Button>
      </Panel>
    );
  }
  return <VoiceForm data={query.data} />;
}

function VoiceForm({ data }: { data: VoicesResponse }) {
  const { can } = usePermissions();
  const mayWrite = can("policy.write");
  const [voiceId, setVoiceId] = useState<string | null>(data.chosenVoiceId);
  const [delivery, setDelivery] = useState<VoiceDelivery>(() => ({ ...data.delivery }));
  const [script, setScript] = useState(data.script);
  const [sampling, setSampling] = useState(false);
  const [sampleError, setSampleError] = useState("");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  const save = useMutate<{ voiceId: string }, void>({
    mutationFn: () => api.voices.choose(voiceId!, delivery),
    success: "Voice saved",
    invalidate: [["voices"]],
  });

  async function audition() {
    if (data.catalogueLimited) return;
    setSampleError("");
    setSampling(true);
    try {
      const url = await api.voices.sample(voiceId ?? undefined, delivery, script.trim() || undefined);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      if (audioRef.current) {
        audioRef.current.src = url;
        await audioRef.current.play();
      }
    } catch {
      setSampleError("The voice preview could not be produced. The catalogue key may be unconfigured.");
    } finally {
      setSampling(false);
    }
  }

  const chosenUnknown = data.chosenIsUnknown && voiceId === data.chosenVoiceId;

  return (
    <>
      <PageHeader
        eyebrow="Configure"
        title="The voice of your company"
        description="Choose a voice, tune its delivery, and hear the words before saving."
        actions={
          <Button
            variant="primary"
            disabled={!mayWrite || !voiceId}
            loading={save.isPending}
            onClick={() => voiceId && save.mutate()}
          >
            Save voice
          </Button>
        }
      />
      {data.catalogueLimited && (
        <Notice tone="amber">
          {data.catalogueNote || "The voice catalogue key is unconfigured — you can choose a voice, but auditioning is unavailable."}
        </Notice>
      )}
      {chosenUnknown && (
        <Notice>
          Your current voice isn’t in the catalogue below. It stays selected until you pick another — it will not be
          silently replaced.
        </Notice>
      )}
      <div className="voice-grid">
        {data.voices.map((v: VoiceOption) => {
          const selected = voiceId === v.id;
          return (
            <button
              className={`voice-choice ${selected ? "selected" : ""}`}
              aria-pressed={selected}
              key={v.id}
              disabled={!mayWrite}
              onClick={() => setVoiceId(v.id)}
            >
              <span className="voice-avatar">{v.name[0]}</span>
              <span>
                <strong>{v.name}</strong>
                <small>
                  {[v.accent, v.gender, v.age].filter(Boolean).join(" · ") || v.note || v.traits.join(" · ") || "Voice"}
                </small>
              </span>
              {selected && <Icon name="success" />}
            </button>
          );
        })}
      </div>
      <div className="grid-two margin-top">
        <Panel title="Delivery">
          <div className="panel-body">
            {data.deliveryRanges.map((r) => (
              <Field key={r.key} label={r.label} hint={r.hint}>
                <span className="range-label">{delivery[r.key].toFixed(2)}</span>
                <input
                  type="range"
                  min={r.min}
                  max={r.max}
                  step={r.step}
                  aria-label={r.label}
                  disabled={!mayWrite}
                  value={delivery[r.key]}
                  onChange={(e) => setDelivery((d) => ({ ...d, [r.key]: Number(e.target.value) }))}
                />
              </Field>
            ))}
          </div>
        </Panel>
        <Panel title="Try the wording">
          <div className="panel-body">
            <Field label="Preview script">
              <textarea rows={5} value={script} onChange={(e) => setScript(e.target.value)} />
            </Field>
            <div className="button-row">
              <Button
                icon="play"
                variant="primary"
                disabled={data.catalogueLimited || !voiceId || sampling}
                loading={sampling}
                onClick={() => void audition()}
              >
                Audition this voice
              </Button>
            </div>
            {/* Provider audition comes back through our origin as audio bytes (CSP blocks the cross-origin clip). */}
            <audio ref={audioRef} hidden />
            {sampleError && <Notice tone="red">{sampleError}</Notice>}
            {!data.catalogueLimited && (
              <p className="muted">
                Plays the selected voice with the current delivery — not your device voice.
              </p>
            )}
          </div>
        </Panel>
      </div>
    </>
  );
}
const integStatusTone = (s: IntegrationItem["status"]): "green" | "amber" | "red" | "neutral" =>
  s === "verified" ? "green" : s === "unverified" ? "amber" : s === "error" ? "red" : "neutral";
const integStatusLabel = (s: IntegrationItem["status"]): string =>
  s === "verified" ? "Verified" : s === "unverified" ? "Needs verification" : s === "error" ? "Error" : "Disconnected";
const integSymbol = (kind: IntegrationItem["kind"]): string =>
  kind === "nms" ? "network" : kind === "voice_subaccount" ? "phone" : "radio";

type Available = IntegrationsResponse["available"][number];

function Integrations() {
  const { can } = usePermissions();
  const mayWrite = can("integrations.write");
  const [connecting, setConnecting] = useState<Available | null>(null);
  const [secret, setSecret] = useState("");
  const [label, setLabel] = useState("");
  // The non-secret half of a connect form — e.g. Sonar's instance address. Rendered from the
  // connector's requiredConfig so a new adapter's fields appear without a console change.
  const [cfg, setCfg] = useState<Record<string, string>>({});
  const [revoking, setRevoking] = useState<IntegrationItem | null>(null);

  const query = useResource<IntegrationsResponse>(qk.integrations, () => api.integrations(), {
    staleTimeMs: 30_000,
  });

  const verify = useMutate<IntegrationItem, string>({
    mutationFn: (id) => api.verifyIntegration(id),
    success: (item) => (item.status === "verified" ? "Connection verified" : "Verification ran"),
    invalidate: [qk.integrations],
  });
  const connect = useMutate<IntegrationItem, ConnectIntegrationRequest>({
    mutationFn: (body) => api.connectIntegration(body),
    success: "Connection added — verify it next",
    invalidate: [qk.integrations],
    onSuccess: () => {
      setConnecting(null);
      setSecret("");
      setLabel("");
      setCfg({});
    },
  });
  const revoke = useMutate<IntegrationItem | { ok: true }, string>({
    mutationFn: (id) => api.revokeIntegration(id),
    success: "Connection revoked",
    invalidate: [qk.integrations],
    onSuccess: () => setRevoking(null),
  });

  if (query.isLoading) {
    return (
      <div className="page-status" role="status" aria-live="polite">
        <Icon name="loader" className="spin" size={26} />
        <p>Loading integrations…</p>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Panel title="Integrations unavailable">
        <p className="muted">{(query.error as ApiError | null)?.detail || "The console could not reach NightDesk."}</p>
        <Button variant="primary" loading={query.isFetching} onClick={() => void query.refetch()}>
          <Icon name="refresh" /> Try again
        </Button>
      </Panel>
    );
  }

  const { items, platform, available } = query.data;
  const tenantItems = items.filter((i) => i.scope === "tenant");
  const connectable = available.filter((a) => a.kind === "bss" || a.kind === "nms");
  // A connection is named the way the catalogue names it ("Billing system (REST API)"), never
  // by its adapter id (bss_rest), and its kind in words rather than "BSS".
  const connLabel = (adapter: string): string =>
    available.find((a) => a.adapter === adapter)?.label ??
    adapter.replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  const KIND_LABEL: Record<string, string> = {
    bss: "Billing system",
    nms: "Network monitoring",
    voice_subaccount: "Phone carrier",
  };
  // One honest line: the stored credential and its last check. "No credential on file ·
  // verified" read as a contradiction - a verification without a credential is stale.
  const credLine = (i: (typeof items)[number]): string => {
    if (!i.last4) return "No credential on file";
    if (i.lastError) return `••••${i.last4} · ${i.lastError}`;
    return i.status === "verified" && i.lastVerifiedAt ? `••••${i.last4} · verified` : `••••${i.last4}`;
  };

  // Required non-secret fields for the connector being connected, and whether they are filled.
  const reqCfg = connecting?.requiredConfig ?? [];
  const cfgOk = reqCfg.every((f) => !f.required || (cfg[f.key] ?? "").trim().length > 0);

  return (
    <>
      <PageHeader
        eyebrow="Configure"
        title="Connect your systems"
        description="Give the assistant the context it needs to help your callers."
      />

      <Panel title="Your connections">
        {tenantItems.length ? (
          tenantItems.map((i) => (
            <article className="record-row" key={i.id}>
              <span className="integration-symbol">
                <Icon name={integSymbol(i.kind)} size={27} />
              </span>
              <div className="record-body">
                <div className="record-heading">
                  <h3>{connLabel(i.adapter)}</h3>
                  <Badge tone={integStatusTone(i.status)}>{integStatusLabel(i.status)}</Badge>
                </div>
                <p>{KIND_LABEL[i.kind] ?? i.kind}</p>
                <small className="muted">{credLine(i)}</small>
              </div>
              <div className="button-row">
                <Button
                  disabled={!mayWrite || (verify.isPending && verify.variables === i.id)}
                  loading={verify.isPending && verify.variables === i.id}
                  onClick={() => verify.mutate(i.id)}
                >
                  Verify
                </Button>
                <Button variant="ghost" disabled={!mayWrite} onClick={() => setRevoking(i)}>
                  Revoke
                </Button>
              </div>
            </article>
          ))
        ) : (
          <EmptyState
            icon="link"
            title="No connections yet"
            text="Connect your billing or monitoring system below so the assistant can look up accounts and outages."
          />
        )}
      </Panel>

      {connectable.length > 0 && (
        <Panel className="margin-top" title="Available to connect">
          <div className="panel-body">
            <div className="grid-two">
              {connectable.map((a) => (
                <div className="list-row" key={`${a.kind}:${a.adapter}`}>
                  <Icon name={integSymbol(a.kind as IntegrationItem["kind"])} />
                  <span>
                    <strong>{a.label}</strong>
                    <small>{KIND_LABEL[a.kind] ?? a.kind}</small>
                  </span>
                  <Button
                    variant="primary"
                    disabled={!mayWrite}
                    onClick={() => {
                      setConnecting(a);
                      setSecret("");
                      setLabel(a.label);
                      setCfg({});
                    }}
                  >
                    Connect
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </Panel>
      )}

      <Panel className="margin-top" title="Included with NightDesk" action={<Badge>Read-only</Badge>}>
        <div className="panel-body">
          <div className="grid-two">
            {platform.map((p) => (
              <div className="list-row" key={p.key}>
                <Icon name="shield" />
                <span>
                  <strong>{p.label}</strong>
                  <small>{p.note}</small>
                </span>
                <Badge tone={p.configured ? "green" : "neutral"}>{p.configured ? "Managed" : "Not set"}</Badge>
              </div>
            ))}
          </div>
        </div>
      </Panel>

      <Modal
        open={Boolean(connecting)}
        onClose={() => setConnecting(null)}
        title={connecting ? `Connect ${connecting.label}` : "Connect an integration"}
        description="Generate a scoped, read-only token in your vendor console and paste it here. Never email a credential."
        footer={
          <>
            <Button onClick={() => setConnecting(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!secret.trim() || !cfgOk || !mayWrite || connect.isPending}
              loading={connect.isPending}
              onClick={() =>
                connecting &&
                connect.mutate({
                  kind: connecting.kind as ConnectIntegrationRequest["kind"],
                  adapter: connecting.adapter,
                  secret: secret.trim(),
                  ...(label.trim() ? { label: label.trim() } : {}),
                  ...(reqCfg.length
                    ? {
                        config: Object.fromEntries(
                          reqCfg.map((f) => [f.key, (cfg[f.key] ?? "").trim()]),
                        ),
                      }
                    : {}),
                })
              }
            >
              Connect
            </Button>
          </>
        }
      >
        {connecting?.instructions?.length ? (
          <ol className="step-list">
            {connecting.instructions.map((step, i) => (
              <li key={i}>
                <span className="step-index">{i + 1}</span>
                <div>{step}</div>
              </li>
            ))}
          </ol>
        ) : null}
        {reqCfg.map((f) => (
          <Field key={f.key} label={f.label} hint={f.example ? `For example: ${f.example}` : undefined}>
            <input
              value={cfg[f.key] ?? ""}
              placeholder={f.example}
              onChange={(e) => setCfg((c) => ({ ...c, [f.key]: e.target.value }))}
            />
          </Field>
        ))}
        <Field label="Label (optional)">
          <input value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <Field label="Scoped read-only token">
          <input
            type="password"
            autoComplete="off"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="Paste the token from your vendor console"
          />
        </Field>
        {connect.isError && (
          <Notice tone="red">{(connect.error as ApiError | null)?.detail || "That connection could not be saved."}</Notice>
        )}
      </Modal>

      <Modal
        open={Boolean(revoking)}
        onClose={() => setRevoking(null)}
        title="Revoke this connection?"
        description="Account or network lookups from this source will stop."
        footer={
          <>
            <Button onClick={() => setRevoking(null)}>Keep connected</Button>
            <Button
              variant="danger"
              disabled={!mayWrite || revoke.isPending}
              loading={revoke.isPending}
              onClick={() => revoking && revoke.mutate(revoking.id)}
            >
              Revoke
            </Button>
          </>
        }
      >
        <p>{revoking ? `${revoking.adapter} (${revoking.kind.toUpperCase()})` : ""}</p>
      </Modal>
    </>
  );
}
function Settings() {
  const { can } = usePermissions();
  const { demo, platform, tenant } = useDemoMode();
  const session = useSession();
  const mayWrite = can("users.write");
  const [invite, setInvite] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("viewer");

  const usersQ = useResource(qk.users, () => api.users(), { staleTimeMs: 30_000 });
  const brandsQ = useResource(qk.brands, () => api.brands(), { staleTimeMs: 60_000 });
  const numbersQ = useResource(qk.numbers, () => api.numbers(), { staleTimeMs: 60_000 });

  const inviteM = useMutate<UserItem, InviteUserRequest>({
    mutationFn: (body) => api.inviteUser(body),
    success: "Teammate invited",
    invalidate: [qk.users],
    onSuccess: () => setInvite(false),
  });
  const updateM = useMutate<UserItem, { id: string; body: UpdateUserRequest }>({
    mutationFn: ({ id, body }) => api.updateUser(id, body),
    success: "Teammate updated",
    invalidate: [qk.users],
  });

  const [newE164, setNewE164] = useState("");
  const [newKind, setNewKind] = useState<AddNumberRequest["kind"]>("after_hours");
  const [newLabel, setNewLabel] = useState("");
  const addNumberM = useMutate<{ ok: true; id: string }, AddNumberRequest>({
    mutationFn: (body) => api.addNumber(body),
    success: "Number added",
    invalidate: [qk.numbers, qk.setup],
    onSuccess: () => {
      setNewE164("");
      setNewLabel("");
    },
  });
  const deleteNumberM = useMutate<{ ok: true }, string>({
    mutationFn: (id) => api.deleteNumber(id),
    success: "Number removed",
    invalidate: [qk.numbers, qk.setup],
  });

  const users = itemsOf(usersQ.data);
  const brands = itemsOf(brandsQ.data);
  const numbers = itemsOf(numbersQ.data);
  const selfId = session?.user.id;

  return (
    <>
      <PageHeader
        eyebrow="Configure"
        title="Workspace settings"
        description="Your team, company identity, and phone numbers."
      />
      <div className="grid-two">
        <Panel title="Company">
          <div className="panel-body">
            <dl className="detail-list">
              <div>
                <dt>Company</dt>
                <dd>{session?.tenant.name ?? "—"}</dd>
              </div>
              <div>
                <dt>Time zone</dt>
                <dd>{session?.tenant.timezone ?? "—"}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{session?.tenant.status ?? "—"}</dd>
              </div>
            </dl>
            <Link to="/voices" className="text-button">
              Manage voice <Icon name="arrow" size={16} />
            </Link>
            <p className="readonly-note">
              <Icon name="lock" size={15} />
              Company identity is managed by NightDesk — contact support to change it.
            </p>
          </div>
        </Panel>
        <Panel title="Outbound mode">
          <div className="panel-body">
            <Badge tone={demo ? "amber" : "green"}>{demo ? "Demo mode — outbound simulated" : "Live"}</Badge>
            <p className="muted" style={{ marginTop: 12 }}>
              {platform
                ? "The platform demo switch is on: no calls, texts, or emails leave the building."
                : tenant
                  ? "This workspace is in demo mode: outbound is simulated until you go live."
                  : "Outbound is live. Calls, texts, and pages are delivered."}
            </p>
            <p className="readonly-note">
              <Icon name="lock" size={15} />
              Going live is a guarded action taken from the demo banner’s “Go live…”.
            </p>
          </div>
        </Panel>
      </div>

      <Panel className="margin-top" title="Your team">
        <p className="muted" style={{ marginTop: 0 }}>
          People management moved to its own page — create accounts, set and reset passwords,
          roles, and on-call eligibility all live there now.
        </p>
        <Link to="/people" className="button">
          <Icon name="users" size={16} /> Open People
        </Link>
      </Panel>

      <div className="grid-two margin-top">
        <Panel title="Brands" action={<Badge>Read-only</Badge>}>
          <div className="panel-body">
            {brands.length ? (
              brands.map((b: BrandItem) => (
                <div className="list-row" key={b.id}>
                  <Icon name="shield" />
                  <span>
                    <strong>{b.name}</strong>
                    <small>{b.recordingDisclosureText ? "Recording disclosure set" : "No recording disclosure"}</small>
                  </span>
                  {b.isDefault && <Badge tone="blue">Default</Badge>}
                </div>
              ))
            ) : (
              <p className="muted">No brands.</p>
            )}
          </div>
        </Panel>
        <Panel title="Phone numbers">
          <div className="panel-body">
            {numbers.length ? (
              numbers.map((n: PhoneNumberItem) => (
                <div className="list-row" key={n.id}>
                  <Icon name="phone" />
                  <span>
                    <strong>{n.label || n.e164}</strong>
                    <small>
                      {n.e164} · {n.kind}
                    </small>
                  </span>
                  <Badge tone={n.active ? "green" : "neutral"}>{n.active ? "Active" : "Off"}</Badge>
                  {mayWrite && (
                    <Button
                      variant="ghost"
                      icon="trash"
                      disabled={deleteNumberM.isPending}
                      onClick={() => {
                        if (window.confirm(`Remove ${n.e164} from this workspace?`)) deleteNumberM.mutate(n.id);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))
            ) : (
              <p className="muted">No numbers yet. Add the number callers dial after hours below.</p>
            )}

            {mayWrite && (
              <form
                className="number-add"
                onSubmit={(e) => {
                  e.preventDefault();
                  const e164 = newE164.trim();
                  if (!e164) return;
                  addNumberM.mutate({ e164, kind: newKind, ...(newLabel.trim() ? { label: newLabel.trim() } : {}) });
                }}
              >
                <Field label="Number">
                  <input
                    placeholder="+13045551234"
                    value={newE164}
                    onChange={(e) => setNewE164(e.target.value)}
                  />
                </Field>
                <Field label="Kind">
                  <select value={newKind} onChange={(e) => setNewKind(e.target.value as AddNumberRequest["kind"])}>
                    <option value="after_hours">After-hours</option>
                    <option value="overflow">Overflow</option>
                    <option value="demo">Demo</option>
                    <option value="health">Health check</option>
                  </select>
                </Field>
                <Field label="Label (optional)">
                  <input
                    placeholder="Main after-hours line"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                  />
                </Field>
                <Button type="submit" variant="primary" icon="plus" loading={addNumberM.isPending} disabled={!newE164.trim()}>
                  Add number
                </Button>
              </form>
            )}
          </div>
        </Panel>
      </div>

      <Modal
        open={invite}
        onClose={() => setInvite(false)}
        title="Invite a teammate"
        description="They receive an email invitation to join this workspace."
        footer={
          <>
            <Button onClick={() => setInvite(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!mayWrite || !name.trim() || !email.trim()}
              loading={inviteM.isPending}
              onClick={() => inviteM.mutate({ name: name.trim(), email: email.trim(), role })}
            >
              Send invitation
            </Button>
          </>
        }
      >
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Role">
          <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
        </Field>
        {inviteM.isError && (
          <Notice tone="red">{(inviteM.error as ApiError | null)?.detail || "That invitation could not be sent."}</Notice>
        )}
      </Modal>
    </>
  );
}
